import {
  ControlledCKEditor,
  ControlledNumberField,
  ControlledTextField,
  UserSelector,
} from '@issuemanager/apps/client/components';
import {
  ACTION_TITLE_LENGTH_MAX,
  MAX_DUE_DATE_DAYS,
} from '@issuemanager/constants';
import { useEventCallback } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Controller, UseFormReturn, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { ExtendedCreateIssueTemplateDto } from './use-issue-template-form';

interface UseActionsFormProps {
  form: UseFormReturn<ExtendedCreateIssueTemplateDto>;
  defaultValues: ExtendedCreateIssueTemplateDto;
}

export const useActionsForm = ({
  form,
  defaultValues,
}: UseActionsFormProps) => {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [operation, setOperation] = useState<string>('');

  const { t } = useTranslation(['settings']);

  const actionFieldArray = useFieldArray({
    name: 'actions',
    control: form.control,
  });

  const [actions, isAutomatic] = form.watch(['actions', 'isAutomatic']);

  useEffect(() => {
    const { actions } = defaultValues;
    if (actions && actions.length > 0) {
      setExpanded((prevExpanded) => {
        const newExpanded = { ...prevExpanded };
        actions.forEach((action) => {
          if (action?.customId && !prevExpanded[action.customId]) {
            newExpanded[action.customId] = true;
          }
        });
        return newExpanded;
      });
    }
  }, [defaultValues.actions]);
  const handleAddAction = () => {
    actionFieldArray.append({
      title: '',
      description: null,
      dueDate: null,
      ownerId: null,
      sequence: actionFieldArray.fields.length,
      customId: uuidv4(),
      // NOTE: related entities will be used in the future so far are not needed. Ticket RCP-12663
      // govmapGroups: [],
    });
    setOperation('append');
  };

  const handleExpand = useEventCallback((id: string) => {
    if (expanded[id]) {
      setExpanded((prevExpanded) => ({ ...prevExpanded, [id]: false }));
    } else {
      setExpanded((prevExpanded) => ({ ...prevExpanded, [id]: true }));
    }
  });

  const handleDelete = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    index: number,
    customId: string,
  ) => {
    e.stopPropagation();

    setExpanded((prevExpanded) => {
      const newExpanded = { ...prevExpanded };
      delete newExpanded[customId];
      return newExpanded;
    });
    actionFieldArray.remove(index);

    // NOTE: we need to force the form.actions to re-run validations over the whole array.
    // If not, error will remain after another change.
    // https://github.com/react-hook-form/react-hook-form/issues/1834
    // for consistency, only trigger when form is submitted
    if (form.formState.isSubmitted) {
      form.trigger('actions');
    }
  };

  useEffect(() => {
    if (operation === 'append') {
      const lastAction =
        actionFieldArray.fields[actionFieldArray.fields.length - 1];
      handleExpand(lastAction.customId);
      setOperation('');
    }
  }, [operation, actionFieldArray.fields, handleExpand]);

  const labels = {
    title: t('settings:issueTemplates.form.labels.action.title'),
    description: t('settings:issueTemplates.form.labels.action.description'),
    dueDate: t('settings:issueTemplates.form.labels.action.dueDate'),
    ownerId: t('settings:issueTemplates.form.labels.action.owner'),
    approverId: t('settings:issueTemplates.form.labels.action.approver'),
    // NOTE: related entities will be used in the future so far are not needed. Ticket RCP-12663
    // entities: t('settings:issueTemplates.form.labels.action.entities'),
  };

  let labelsObj = {};

  const actionFields = actionFieldArray.fields.map((field, index) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const names: Record<string, any> = {
      title: `actions.${index}.title`,
      description: `actions.${index}.description`,
      dueDate: `actions.${index}.dueDate`,
      ownerId: `actions.${index}.ownerId`,
      approverId: `actions.${index}.approverId`,
      // NOTE: related entities will be used in the future so far are not needed. Ticket RCP-12663
      // govmapGroups: `actions.${index}.govmapGroups`,
    };

    labelsObj = {
      ...labelsObj,
      [names.title]: labels.title,
      [names.description]: labels.description,
      [names.dueDate]: labels.dueDate,
      [names.ownerId]: labels.ownerId,
      [names.approverId]: labels.approverId,
      // NOTE: related entities will be used in the future so far are not needed. Ticket RCP-12663
      // [names.govmapGroups]: labels.entities,
    };

    const actionErrors = form.formState.errors.actions?.[index];

    return {
      id: field.id,
      handleDelete: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) =>
        handleDelete(e, index, field.customId),
      expanded: expanded[field.customId] ?? false,
      handleExpand: () => handleExpand(field.customId),
      readOnly: false,
      isInProgress: false,
      isOpen: false,
      isToBeApproved: false,
      isDraft: true,
      isPending: false,
      sequence: field.sequence ?? null,
      errors: actionErrors,
      title: (
        <ControlledTextField
          name={names.title}
          label={labels.title}
          labelInfo={t(
            'settings:issueTemplates.form.info.action.title.mustBeUnique',
          )}
          control={form.control}
          rules={{ required: true }}
          data-testid='title'
          onChange={() => {
            if (form.formState.isSubmitted) {
              form.trigger(`actions.${index}.title`);

              actions.forEach((action, actionIndex) => {
                if (
                  actionIndex !== index &&
                  action.title &&
                  action.title.trim()
                ) {
                  form.trigger(`actions.${actionIndex}.title`);
                }
              });
            } else {
              actions.forEach((action, actionIndex) => {
                if (
                  actionIndex !== index &&
                  action.title &&
                  action.title.trim()
                ) {
                  form.trigger(`actions.${actionIndex}.title`);
                }
              });
            }
          }}
          maxLength={ACTION_TITLE_LENGTH_MAX}
        />
      ),
      description: (
        <ControlledCKEditor
          name={names.description}
          label={labels.description}
          control={form.control}
          rules={{ required: isAutomatic }}
          testId='description'
        />
      ),
      dueDate: (
        <ControlledNumberField
          name={names.dueDate}
          label={labels.dueDate}
          labelInfo={t(
            'settings:issueTemplates.form.info.action.dueDate.mustBeEarlierThanIssueDueDate',
          )}
          control={form.control}
          rules={{
            required: isAutomatic,
            deps: actions.map((_, i) => `actions.${i}.dueDate` as const),
          }}
          min={0}
          max={MAX_DUE_DATE_DAYS}
          data-testid='dueDate'
        />
      ),
      ownerId: (
        <Controller
          name={names.ownerId}
          control={form.control}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <UserSelector
              value={value}
              initialValue={field.owner?.id}
              onChange={onChange}
              TextFieldProps={{
                required: isAutomatic,
                label: labels.ownerId,
                'data-testid': 'ownerId',
                error: !!error?.message,
                helperText: error?.message,
                inputProps: {
                  'data-testid': 'ownerId-input',
                },
              }}
            />
          )}
        />
      ),
      approverId: (
        <Controller
          name={names.approverId}
          control={form.control}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <UserSelector
              initialValue={field.approver?.id}
              value={value}
              onChange={onChange}
              TextFieldProps={{
                label: labels.approverId,
                'data-testid': 'approverId',
                error: !!error?.message,
                helperText: error?.message,
                inputProps: {
                  'data-testid': 'approverId-input',
                },
              }}
            />
          )}
        />
      ),
      actionAttachments: null,
      /*
        //NOTE: related entities will be used in the future so far are not needed. Ticket RCP-12663
        govmapGroups: (
          <Controller
            name={names.govmapGroups}
            control={form.control}
            render={({ field: { value, onChange }, fieldState: { error } }) => {
              return (
                <GovmapGroupsSelector
                  value={value}
                  onChange={onChange}
                  label={labels.entities}
                  error={!!error?.message}
                  helperText={error?.message}
                />
              );
            }}
          />
        ),
      */
    };
  });

  return {
    actionFields,
    handleAddAction,
    actions,
    actionFormSetFocus: form.setFocus,
  };
};
